import * as React from "react";
import { cn } from "../../lib/utils";
import { Text } from "../Text";
import { IconButton } from "../IconButton";
import { Divider } from "../Divider";
import { Icon } from "../../lib/icons";
import type { IconName } from "../../lib/icons";

export interface SidePanelProps {
  /**
   * Whether the side panel is open
   */
  isOpen: boolean;
  /**
   * Callback when the side panel should close
   */
  onClose?: () => void;
  /**
   * Side panel title - optional
   */
  title?: string;
  /**
   * Icon to display before the title - optional
   */
  titleIcon?: IconName | React.ReactNode;
  /**
   * Side panel description/subtitle - optional
   */
  description?: string;
  /**
   * Side panel footer content - optional
   */
  footer?: React.ReactNode;
  /**
   * Side panel body content
   */
  children: React.ReactNode;
  /**
   * Position from which the side panel appears
   */
  position?: "left" | "right" | "center";
  /**
   * Width variant or custom width
   */
  width?: "small" | "medium" | "large" | "xlarge" | string;
  /**
   * Whether to show the close button in header
   */
  showCloseButton?: boolean;
  /**
   * Additional header actions (e.g., expand, minimize buttons) - optional
   */
  headerActions?: React.ReactNode;
  /**
   * Whether clicking the overlay closes the side panel
   */
  closeOnOverlayClick?: boolean;
  /**
   * Whether pressing Escape closes the side panel
   */
  closeOnEscape?: boolean;
  /**
   * Custom class name for the side panel container
   */
  className?: string;
  /**
   * Custom class name for the side panel content
   */
  contentClassName?: string;
  /**
   * Custom class name for the header
   */
  headerClassName?: string;
  /**
   * Custom class name for the body
   */
  bodyClassName?: string;
  /**
   * Custom class name for the footer
   */
  footerClassName?: string;
  /**
   * Custom class name for the overlay
   */
  overlayClassName?: string;
  /**
   * ARIA label for accessibility
   */
  ariaLabel?: string;
  /**
   * ARIA description for accessibility
   */
  ariaDescribedBy?: string;
}

const SidePanel = React.forwardRef<HTMLDivElement, SidePanelProps>(
  (
    {
      isOpen,
      onClose,
      title,
      titleIcon,
      description,
      footer,
      children,
      position = "right",
      width = "medium",
      showCloseButton = true,
      headerActions,
      closeOnOverlayClick = true,
      closeOnEscape = true,
      className,
      contentClassName,
      headerClassName,
      bodyClassName,
      footerClassName,
      overlayClassName,
      ariaLabel,
      ariaDescribedBy,
    },
    ref
  ) => {
    const panelRef = React.useRef<HTMLDivElement>(null);
    const contentRef = ref || panelRef;
    const [isVisible, setIsVisible] = React.useState(false);
    const [shouldRender, setShouldRender] = React.useState(false);

    // Width configurations
    const widthConfig = {
      small: "w-80",
      medium: "w-96",
      large: "w-[32rem]",
      xlarge: "w-[40rem]",
    };

    // Handle mounting and unmounting with animation
    React.useEffect(() => {
      if (isOpen) {
        setShouldRender(true);
        // Small delay to trigger animation
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      } else {
        setIsVisible(false);
        // Wait for animation to complete before unmounting
        const timer = setTimeout(() => {
          setShouldRender(false);
        }, 300); // Match animation duration
        return () => clearTimeout(timer);
      }
    }, [isOpen]);

    // Position-based classes
    const positionClasses = {
      left: {
        container: "justify-start",
        panel: isVisible ? "animate-slide-in-left" : "animate-slide-out-left",
      },
      right: {
        container: "justify-end",
        panel: isVisible ? "animate-slide-in-right" : "animate-slide-out-right",
      },
      center: {
        container: "justify-center items-center",
        panel: isVisible ? "animate-scale-in" : "animate-scale-out",
      },
    };

    // Handle escape key
    React.useEffect(() => {
      if (!isOpen || !closeOnEscape || !onClose) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, closeOnEscape, onClose]);

    // Prevent body scroll when side panel is open
    React.useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }

      return () => {
        document.body.style.overflow = "";
      };
    }, [isOpen]);

    // Handle overlay click
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnOverlayClick && e.target === e.currentTarget && onClose) {
        onClose();
      }
    };

    // Don't render if not open and animation is complete
    if (!shouldRender) return null;

    const hasHeader = title || description;
    const widthClass =
      width in widthConfig
        ? widthConfig[width as keyof typeof widthConfig]
        : width;

    return (
      <div
        className={cn(
          "fixed inset-0 z-10000 flex p-4",
          positionClasses[position].container,
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title}
        aria-describedby={ariaDescribedBy}
      >
        {/* Overlay with blur */}
        <div
          className={cn(
            "absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300",
            isVisible ? "opacity-100" : "opacity-0",
            overlayClassName
          )}
          onClick={handleOverlayClick}
          aria-hidden="true"
        />

        {/* Side Panel Content */}
        <div
          ref={contentRef}
          className={cn(
            "relative bg-white shadow-2xl transition-transform duration-300 ease-out rounded-large",
            "flex flex-col max-w-full overflow-hidden",
            position === "center" ? "max-h-[90vh]" : "h-full",
            widthClass,
            positionClasses[position].panel,
            contentClassName
          )}
        >
          {/* Header */}
          {hasHeader && (
            <div
              className={cn(
                "flex items-center justify-between gap-4 px-7 py-5 shrink-0 border-b border-surface-outline-neutral-subtle",
                headerClassName
              )}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {titleIcon && (
                  <div className="flex items-center justify-center shrink-0">
                    {typeof titleIcon === "string" ? (
                      <Icon name={titleIcon as IconName} size={20} />
                    ) : (
                      titleIcon
                    )}
                  </div>
                )}
                <div className="flex flex-col flex-1 min-w-0">
                  {title && (
                    <Text
                      as="h2"
                      variant="body"
                      size="large"
                      weight="semibold"
                      color="default"
                      className="truncate"
                    >
                      {title}
                    </Text>
                  )}
                  {description && (
                    <Text
                      as="p"
                      variant="body"
                      size="small"
                      weight="regular"
                      color="subtle"
                      className="mt-0.5"
                    >
                      {description}
                    </Text>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {headerActions}
                {showCloseButton && onClose && (
                  <IconButton
                    icon="close"
                    onClick={onClose}
                    color="neutral"
                    size="small"
                    aria-label="Close side panel"
                  />
                )}
              </div>
            </div>
          )}

          {/* Header with close button only (no title/description) */}
          {!hasHeader && showCloseButton && onClose && (
            <div className="absolute top-4 right-4 z-10">
              <IconButton
                icon="close"
                onClick={onClose}
                color="neutral"
                size="small"
                aria-label="Close side panel"
              />
            </div>
          )}

          {/* Body */}
          <div
            className={cn(
              "flex-1 overflow-y-auto px-6",
              hasHeader ? "py-6" : "pt-6 pb-4",
              !footer && "pb-6",
              bodyClassName
            )}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex flex-col shrink-0">
              <Divider thickness="thin" variant="muted" />
              <div
                className={cn(
                  "flex items-center justify-end gap-3 px-6 py-4",
                  footerClassName
                )}
              >
                {footer}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

SidePanel.displayName = "SidePanel";

export { SidePanel };
